/* =============================================================================
   VIEWS / ADMIN / TABLE-SIGNALS — Hinweisband „Tisch 4 ruft" (S245 Cut C)
   =============================================================================
   Eine Komponente für zwei Tabs (Belegung und Bestellungen). Bewusst EIN Modul
   mit EINEM Loader und EINEM Realtime-Abo: zwei Tabs, die dieselbe Liste
   getrennt laden, driften auseinander — und ein „Erledigt" auf dem einen wäre
   auf dem anderen noch da.

   Warum die Signale nicht mehr aus `audit_log` kommen (S244 §6.3, Cut-C-Entscheid):
   `audit_log` ist die aufbewahrungspflichtige Spur. Ein „Service gerufen" von
   14:02, das 30 Sekunden später erledigt ist, hat dort nichts verloren; es lässt
   sich nicht per Realtime abonnieren; und ein Erledigt-Klick muss auf ALLEN
   Geräten verschwinden, nicht nur auf dem, der geklickt hat (T2 aus dem Retest).
   Deshalb `table_signals` — eigene Tabelle, eigener Realtime-Kanal, löschbar.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';

/* Modul-State: die offenen Signale des Tenants. Überlebt Re-Render (kein
   View-Hijack) und wird von beiden Tabs gelesen. */
let _signals = [];
let _loadedAt = 0;
let _inflight = null;
let _unsub = null;
let _visibilityBound = false;   // S246 (T3)
/* Callbacks der montierten Bänder — je Tab einer. Set, damit ein doppeltes
   Mount nicht doppelt zeichnet. */
const _listeners = new Set();

const KIND_META = {
  call_service: { icon: '🔔', label: 'Service gerufen',    cls: 'is-service' },
  call_pay:     { icon: '💶', label: 'Zahlen bitte',       cls: 'is-pay' },
  scan_blocked: { icon: '📱', label: 'hat gescannt',       cls: 'is-scan' }
};

const SCAN_REASON_TEXT = {
  manual:            'Schloss ist zu',
  overdue:           'Belegung überzogen',
  reservation:       'Reservierung steht an',
  ordering_disabled: 'Tisch-Bestellung ist aus',
  no_occupancy:      'kein Gast eingetragen'
};

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Pure — Alter eines Signals als Text.
 *
 * Unter zwei Minuten steht die Uhrzeit („14:02"): das ist präziser und liest
 * sich neben einer Bestellung natürlicher. Ab zwei Minuten kippt es auf „vor N
 * Min", weil dann die WARTEZEIT die Information ist, nicht der Zeitpunkt — wer
 * seit zwölf Minuten winkt, soll auch so aussehen.
 */
export function signalAgeText(createdAt, nowMs) {
  const t = createdAt ? new Date(createdAt).getTime() : NaN;
  if (!Number.isFinite(t)) return '';
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const min = Math.floor((now - t) / 60000);
  if (min < 2) {
    return new Date(t).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  if (min < 60) return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  return `vor ${h} Std`;
}

/**
 * Pure — Realtime-Payload auf die offene Liste anwenden.
 *
 * Ein Signal fliegt raus, sobald es geackt ist (`acked_at` gesetzt) — genau das
 * lässt das Band auf allen Geräten gleichzeitig verschwinden. Ein UPDATE ohne
 * Ack (kommt nicht vor, aber der Fall kostet nichts) ersetzt den Eintrag.
 *
 * @returns {Array} neue Liste (nie dieselbe Referenz, damit Vergleiche greifen)
 */
export function applySignalPayload(list, payload) {
  const cur = Array.isArray(list) ? list.slice() : [];
  const ev = payload && payload.eventType;
  const row = (ev === 'DELETE') ? (payload && payload.old) : (payload && payload.new);
  if (!row || !row.id) return cur;
  const idx = cur.findIndex(s => s.id === row.id);

  if (ev === 'DELETE' || row.acked_at) {
    if (idx >= 0) cur.splice(idx, 1);
    return cur;
  }
  if (idx >= 0) cur[idx] = { ...cur[idx], ...row };
  else cur.push(row);
  // Älteste zuerst — in der Reihenfolge arbeitet man sie ab.
  cur.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  return cur;
}

/** Pure — offene Signale einer Belegung bzw. eines Tisches (für die Kachel). */
export function signalsForTable(list, tableId) {
  return (Array.isArray(list) ? list : []).filter(s => s && s.table_id === tableId);
}

/** Pure — Zeile des Bandes als Text (für Tests und als Single-Source des Wortlauts). */
export function signalLineText(sig, tableName, nowMs) {
  const meta = KIND_META[sig && sig.kind] || KIND_META.scan_blocked;
  const who = sig && sig.guest_name ? ` (${sig.guest_name})` : '';
  const age = signalAgeText(sig && sig.created_at, nowMs);
  const name = tableName || (sig && sig.details && sig.details.table_name) || (sig && sig.table_id) || '?';
  if (sig && sig.kind === 'scan_blocked') {
    const why = SCAN_REASON_TEXT[sig.reason] || 'Grund unbekannt';
    return `Tisch ${name}${age ? ' · ' + age : ''} hat gescannt — nicht freigegeben (${why})`;
  }
  return `Tisch ${name}${age ? ' · ' + age : ''} · ${meta.label}${who}`;
}

/* -------------------------------------------------------------------------- */
/* Laden + Realtime                                                            */
/* -------------------------------------------------------------------------- */

function _adapter() {
  return (window.App && window.App.storage && window.App.storage.tableSignals) || null;
}

/** Offene Signale (Modul-State, nicht neu geladen). */
export function openSignals() { return _signals; }

/**
 * S246 (T2) — auf Änderungen hören, ohne ein Band zu mounten.
 *
 * Die Tisch-Kacheln brauchen dasselbe Ereignis wie das Band, zeichnen aber
 * etwas anderes. Gibt eine Abmelde-Funktion zurück.
 */
export function onSignalsChanged(fn) {
  if (typeof fn !== 'function') return () => {};
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

/**
 * Nachladen. Höchstens alle 15 s — das Realtime-Abo ist der eigentliche Weg,
 * dieser Aufruf ist nur der Catch-up nach Tab-Wechsel oder Reconnect.
 */
export function refreshSignals(opts) {
  const force = !!(opts && opts.force);
  const ad = _adapter();
  if (!ad) return Promise.resolve(_signals);
  if (!force && Date.now() - _loadedAt < 15000) return Promise.resolve(_signals);
  if (_inflight) return _inflight;
  _loadedAt = Date.now();
  _inflight = ad.listOpen()
    .then(rows => {
      _signals = Array.isArray(rows) ? rows : [];
      _notify();
      return _signals;
    })
    .catch(e => { console.warn('[table-signals] laden:', e && e.message); return _signals; })
    .finally(() => { _inflight = null; });
  return _inflight;
}

function _notify() {
  for (const fn of _listeners) {
    try { fn(_signals); } catch (e) { console.warn('[table-signals] Listener:', e); }
  }
}

/** Einmalig das Realtime-Abo anhängen (Master/Follower-Relay über storage). */
function _ensureRealtime() {
  if (_unsub) return;
  const st = window.App && window.App.storage;
  if (!st || typeof st.subscribeTableSignals !== 'function') return;
  try {
    _unsub = st.subscribeTableSignals((payload) => {
      const before = _signals.length;
      _signals = applySignalPayload(_signals, payload);
      _notify();
      // Ton nur bei einem NEUEN Ruf, nicht beim Erledigen — und nur, wenn der
      // Operator die Bestell-Töne ohnehin an hat. Ein gesperrter Scan piept
      // bewusst nicht: er ist ein Hinweis, kein Ruf.
      const row = payload && payload.new;
      if (payload && payload.eventType === 'INSERT' && _signals.length > before
          && row && row.kind !== 'scan_blocked'
          && typeof window.playOrderPing === 'function') {
        try { window.playOrderPing(); } catch (_) { /* Ton ist Kür */ }
      }
    });
  } catch (e) { console.warn('[table-signals] Realtime:', e && e.message); }
}

/**
 * S246 (T3) — Catch-up, wenn der Tab wieder sichtbar wird.
 *
 * Ein Realtime-Ereignis, das ankommt, während der Tab im Hintergrund liegt,
 * wird zwar verarbeitet — aber eine unterbrochene Verbindung merkt das Band von
 * sich aus nie: es zeichnet nur auf Ereignisse. Ohne diesen Haken erschien ein
 * Ruf aus der Lücke erst, wenn zufällig irgendetwas anderes ein Neuzeichnen
 * auslöste. Der Reconnect-Zweig sitzt in storage.js (_fanoutReconnectCatchUp).
 */
function _bindVisibilityCatchUp() {
  if (_visibilityBound || typeof document === 'undefined') return;
  _visibilityBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshSignals({ force: true });
  });
}

/**
 * Ein Band mounten. Der Aufrufer gibt die Container-Id und bekommt bei jeder
 * Änderung ein Neuzeichnen — ohne dass er selbst laden oder abonnieren muss.
 */
export function mountSignalBand(containerId, tableNameFn) {
  _ensureRealtime();
  _bindVisibilityCatchUp();
  const paint = () => renderSignalBandInto(containerId, tableNameFn);
  _listeners.add(paint);
  paint();
  refreshSignals();
  return () => { _listeners.delete(paint); };
}

/**
 * S257 — das Abo gehört an den Boot, nicht an die erste Sicht, die es braucht.
 *
 * Bis hierher entstand der Channel erst, wenn `mountSignalBand()` lief. Der
 * Coordinator hält aber nur EINEN Channel je Browser, und zwar im Master-Tab:
 * stand der Master auf dem Dashboard, existierte der Channel im ganzen Browser
 * nicht, und **kein** Follower bekam je ein Ereignis — ein Service-Ruf kam im
 * zweiten Tab nach 35 Sekunden noch nicht an (S255-Cockpit-Retest §6).
 *
 * `attachRealtimeSync()` ruft das jetzt beim Login, wie die sechs anderen
 * Kanäle. Das Mounten der Sichten bleibt unverändert — `_ensureRealtime()` ist
 * gegen `_unsub` geguarded, es gibt also weiterhin genau ein Abo, egal in
 * welcher Reihenfolge Boot und Sicht kommen.
 *
 * @returns {(function():void)|null} Abmelder, oder `null`, wenn (noch) kein Abo
 *   zustande kam — dann versucht es der nächste `attachRealtimeSync()` erneut,
 *   statt einen Abmelder für nichts zu hinterlassen.
 */
export function startSignalsRealtime() {
  _ensureRealtime();
  _bindVisibilityCatchUp();
  refreshSignals();
  return _unsub ? stopSignalsRealtime : null;
}

/**
 * Gegenstück für `detachRealtimeSync()` (Logout, Tenant-Wechsel).
 *
 * Räumt NUR den Channel ab. Der Sichtbarkeits-Nachlauf bleibt hängen — er ist
 * die zweite Verteidigungslinie, wenn ein Relay ausfällt, und er trägt sich
 * auch dann, wenn gar kein Abo zustande kam (alter Adapter ohne
 * `subscribeTableSignals`). Ihn beim Logout abzuräumen hiesse, ihn beim nächsten
 * Login neu binden zu müssen — für nichts.
 */
export function stopSignalsRealtime() {
  if (!_unsub) return;
  try { _unsub(); } catch (e) { console.warn('[table-signals] Abmelden:', e && e.message); }
  _unsub = null;
}

/** Test-Hook: hängt gerade ein Realtime-Abo? */
export function _hasSignalsRealtimeForTests() { return !!_unsub; }

/** Test-Hook: Abo, Liste, Zeichner und Lade-Drossel zurücksetzen. */
export function _resetSignalsForTests() {
  stopSignalsRealtime();
  _signals = [];
  _loadedAt = 0;
  _inflight = null;
  _listeners.clear();
}

/* -------------------------------------------------------------------------- */
/* Render                                                                      */
/* -------------------------------------------------------------------------- */

/** HTML des Bandes. Leere Liste → leerer String (kein leerer Kasten). */
export function renderSignalBandHtml(list, tableNameFn, nowMs) {
  const rows = Array.isArray(list) ? list : [];
  if (!rows.length) return '';
  const nameOf = typeof tableNameFn === 'function' ? tableNameFn : (() => null);
  return `<div class="occ-scan-band">${rows.map(s => {
    const meta = KIND_META[s.kind] || KIND_META.scan_blocked;
    return `
      <div class="occ-scan-row ${meta.cls}">
        <span>${meta.icon} ${htmlEscape(signalLineText(s, nameOf(s.table_id), nowMs))}</span>
        <button type="button" class="btn btn-ghost btn-sm"
                onclick="ackTableSignal('${htmlEscape(s.id)}')">Erledigt</button>
      </div>`;
  }).join('')}</div>`;
}

/** Zuletzt GESCHRIEBENES Markup je Container — siehe `renderSignalBandInto`. */
const _lastBandHtml = new Map();

/**
 * Non-destruktiv: nur ersetzen, wenn sich das Markup wirklich ändert — sonst
 * baut sich das Band bei jedem Realtime-Tick neu auf.
 *
 * S253 (Beifang aus dem S252-Retest): verglichen wird gegen das, was WIR
 * zuletzt geschrieben haben, nicht gegen `el.innerHTML`. Der naive Vergleich
 * kann hier nicht greifen — `dom-bindings.js` wandelt den `onclick`-Handler der
 * „Erledigt"-Knöpfe in einen echten Listener um und **entfernt danach das
 * Attribut** (CSP-Regel aus S23). Was im DOM steht, ist damit nie wieder das,
 * was hineingeschrieben wurde; der Vergleich schlägt ab dem ersten Durchlauf
 * immer fehl. Dieselbe Wurzel wie Befund B1 auf der Gast-Seite (S252/N2).
 */
export function renderSignalBandInto(containerId, tableNameFn) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const next = renderSignalBandHtml(_signals, tableNameFn);
  // Sicherheitsnetz: hat jemand anders den Container geleert (Tab-Wechsel,
  // Neuaufbau der View), wird trotz gleichem Merker geschrieben.
  const emptied = next !== '' && el.childNodes.length === 0;
  if (!emptied && _lastBandHtml.get(containerId) === next) return;
  el.innerHTML = next;
  _lastBandHtml.set(containerId, next);
}

/* -------------------------------------------------------------------------- */
/* Aktion                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Erledigt. Der Eintrag verschwindet sofort lokal (sonst wirkt der Klick tot,
 * bis das Realtime-Echo zurück ist) und über das Echo dann auf allen Geräten.
 * Schlägt der Aufruf fehl, wird nachgeladen — die Liste soll den DB-Stand
 * zeigen, nicht den Wunsch.
 */
export async function ackTableSignal(signalId) {
  const ad = _adapter();
  if (!ad) return;
  _signals = _signals.filter(s => s.id !== signalId);
  _notify();
  try {
    await ad.ack(signalId);
  } catch (err) {
    console.error('[table-signals] ack:', err);
    if (typeof window.showToast === 'function') {
      window.showToast('Konnte nicht als erledigt markiert werden: ' + (err.message || err), 'error');
    }
    await refreshSignals({ force: true });
  }
}

if (typeof window !== 'undefined') {
  Object.assign(window, { ackTableSignal });
  // S246 (T3) — storage.js zieht das Band im Reconnect-Catch-up nach. Über
  // `window.App`, weil storage.js ein Plain-Script ohne ESM-Importe ist.
  window.App = window.App || {};
  window.App.tableSignals = { refresh: refreshSignals, openSignals };
}
